import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // Check localStorage for backward compatibility, but prefer cookie-based auth
      const token = localStorage.getItem('tour_finder_token');
      const savedUser = localStorage.getItem('tour_finder_user');
      
      // Try to verify with server (which will use cookie if available)
      try {
        const response = await authAPI.getMe();
        setUser(response.data);
        // Update localStorage with fresh user data
        localStorage.setItem('tour_finder_user', JSON.stringify(response.data));
      } catch (error) {
        // If server auth fails, try localStorage fallback
        if (token && savedUser) {
          try {
            const parsedUser = JSON.parse(savedUser);
            // Use saved user data if token exists but API call failed (offline mode)
            setUser(parsedUser);
            console.log('Using cached user data');
          } catch (parseError) {
            // Saved user data is invalid, clear storage
            localStorage.removeItem('tour_finder_token');
            localStorage.removeItem('tour_finder_user');
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, user: userData } = response.data;
    
    // Still store in localStorage for backward compatibility and offline checks
    localStorage.setItem('tour_finder_token', access_token);
    localStorage.setItem('tour_finder_user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await authAPI.register({ name, email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('tour_finder_token', access_token);
    localStorage.setItem('tour_finder_user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const logout = async () => {
    // Call logout endpoint to clear httpOnly cookie
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Also clear localStorage
    localStorage.removeItem('tour_finder_token');
    localStorage.removeItem('tour_finder_user');
    setUser(null);
  };

  const isAdmin = () => user?.role === 'admin';
  const isProvider = () => user?.role === 'provider';

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAdmin,
    isProvider,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
